import { useEffect, useState, type ReactNode } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { useRegenerateCase, useScenario, useScenarioAction, useUpdateCase } from "../api/queries";
import { getScenario } from "../api/simPatient";
import { addSimJob, dismissSimJob, useSimJobs } from "../api/simJobs";
import type { CaseSection, CaseTemplate, Difficulty, ExpectedAction, Scenario } from "../api/types";
import { ActionErrors } from "../components/SimPatient";
import { SimJobProgress } from "../components/SimJobs";
import { Button, Card, ErrorBanner, Loading, Modal, PageHeader, StatusBadge } from "../components/ui";
import { DOMAIN_LABELS, SECTION_LABELS, STATUS_LABELS } from "../lib/sim";

const input = "w-full rounded border px-2 py-1.5 text-sm";

// --- small form primitives ---------------------------------------------------------

function Field({ label, hint, children, className = "" }: { label: string; hint?: string; children: ReactNode; className?: string }) {
  return (
    <label className={`block ${className}`}>
      <span className="text-xs font-medium text-gray-700">{label}</span>
      <div className="mt-1">{children}</div>
      {hint && <span className="mt-0.5 block text-xs text-gray-500">{hint}</span>}
    </label>
  );
}

function Text({ value, onChange, placeholder, rows }: { value: string; onChange: (v: string) => void; placeholder?: string; rows?: number }) {
  return rows ? (
    <textarea rows={rows} className={input} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
  ) : (
    <input className={input} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
  );
}

function NumberInput({ value, onChange, min, max }: { value: number | null; onChange: (v: number) => void; min?: number; max?: number }) {
  return <input type="number" min={min} max={max} className={input} value={value ?? ""} onChange={(e) => onChange(Number(e.target.value))} />;
}

// Comma-separated list edited as free text and split on blur, so typing a
// comma isn't swallowed mid-edit.
function CommaList({ value, onChange, placeholder }: { value: string[]; onChange: (v: string[]) => void; placeholder?: string }) {
  const [text, setText] = useState(value.join(", "));
  return (
    <input
      className={input}
      value={text}
      placeholder={placeholder}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => onChange(text.split(",").map((s) => s.trim()).filter(Boolean))}
    />
  );
}

function RemoveButton({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button type="button" onClick={onClick} aria-label={label} className="shrink-0 rounded px-2 text-sm text-gray-400 hover:bg-gray-100 hover:text-red-600">✕</button>
  );
}

function AddButton({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return <Button type="button" className="px-2 py-1 text-xs" onClick={onClick}>{children}</Button>;
}

// One input per line item, with add/remove.
function StringList({ items, onChange, placeholder, addLabel, rows }: {
  items: string[];
  onChange: (v: string[]) => void;
  placeholder?: string;
  addLabel: string;
  rows?: number;
}) {
  return (
    <div className="space-y-2">
      {items.map((item, i) => (
        <div key={i} className="flex gap-2">
          <span className="w-6 shrink-0 pt-1.5 text-right text-xs text-gray-400">{i + 1}.</span>
          <Text value={item} rows={rows} placeholder={placeholder} onChange={(v) => onChange(items.map((x, j) => (j === i ? v : x)))} />
          <RemoveButton label="Remove" onClick={() => onChange(items.filter((_, j) => j !== i))} />
        </div>
      ))}
      <AddButton onClick={() => onChange([...items, ""])}>{addLabel}</AddButton>
    </div>
  );
}

// --- page ------------------------------------------------------------------------------

export function SimPatientEditorPage() {
  const id = Number(useParams().id);
  const scenario = useScenario(id);
  const s = scenario.data;

  if (!s) {
    return (
      <div className="p-6">
        <ErrorBanner error={scenario.error} />
        {scenario.isLoading && <Loading />}
      </div>
    );
  }
  if (!s.case_template) return <LegacyScenario scenario={s} />;
  return <CaseEditor key={s.id} scenario={s} template={s.case_template} />;
}

// Scenarios written by hand before case templates have nothing to edit here.
function LegacyScenario({ scenario }: { scenario: Scenario }) {
  const regenerate = useRegenerateCase();
  return (
    <div className="p-6">
      <PageHeader title={`Edit: ${scenario.title}`} />
      <Card className="max-w-2xl">
        <p className="text-sm text-gray-700">
          This scenario was written by hand before cases were generated from the AI Simulated Patient Case Template, so it has no template to edit.
        </p>
        <p className="mt-2 text-sm text-gray-700">
          {scenario.course_name
            ? `Generate a full case from ${scenario.course_name} to replace its content.`
            : "Link it to a course first, or generate a new case instead."}
        </p>
        <div className="mt-4 flex gap-2">
          {scenario.course_name && (
            <Button
              variant="primary"
              loading={regenerate.isPending}
              onClick={() =>
                regenerate.mutate(
                  { id: scenario.id },
                  {
                    onSuccess: (res) => {
                      if (res.mode === "queued") {
                        addSimJob({ taskId: res.task_id, kind: "regenerate", label: scenario.title, scenarioId: scenario.id, section: null });
                      }
                    },
                  }
                )
              }
            >
              Generate case from course
            </Button>
          )}
          <Link to="/sim-patients/new" className="inline-flex items-center rounded px-3 py-1.5 text-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">New case</Link>
        </div>
        {regenerate.error != null && <ActionErrors error={regenerate.error} />}
      </Card>
    </div>
  );
}

interface PublishResult {
  published?: boolean;
  errors?: string[];
}

function CaseEditor({ scenario, template }: { scenario: Scenario; template: CaseTemplate }) {
  const location = useLocation();
  const justGenerated = (location.state as { generated?: boolean } | null)?.generated;
  const [form, setForm] = useState<CaseTemplate>(() => structuredClone(template));
  // Bumped when the server replaces the form (save, regenerate) so inputs
  // holding local text, like the comma lists, reset too.
  const [formVersion, setFormVersion] = useState(0);
  const [dirty, setDirty] = useState(false);
  const replaceForm = (next: Scenario) => {
    if (!next.case_template) return;
    setForm(structuredClone(next.case_template));
    setFormVersion((n) => n + 1);
    setDirty(false);
  };
  const [publishResult, setPublishResult] = useState<PublishResult | null>(null);
  const [regenTarget, setRegenTarget] = useState<CaseSection | "all" | null>(null);
  const save = useUpdateCase();
  const action = useScenarioAction();
  const regenerate = useRegenerateCase();
  // A regeneration of this case running in the worker (or just finished).
  const job = [...useSimJobs()].reverse().find((j) => j.kind === "regenerate" && j.scenarioId === scenario.id) ?? null;
  const regenerating = job !== null && !job.outcome;

  // Mutate a draft copy; every edit goes through here.
  const edit = (fn: (draft: CaseTemplate) => void) => {
    setForm((current) => {
      const draft = structuredClone(current);
      fn(draft);
      return draft;
    });
    setDirty(true);
    setPublishResult(null);
  };

  // When the worker finishes, load the regenerated case into the form.
  useEffect(() => {
    if (!job?.outcome?.ok) return;
    let cancelled = false;
    getScenario(scenario.id).then((fresh) => {
      if (cancelled) return;
      replaceForm(fresh);
      setPublishResult(null);
      dismissSimJob(job.taskId);
    });
    return () => {
      cancelled = true;
    };
  }, [job?.outcome?.ok, job?.taskId, scenario.id]);

  const domains = form.critical_decision_points;
  const total = domains.reduce((sum, d) => sum + (Number(d.score) || 0), 0);

  const doSave = async (thenPublish: boolean) => {
    setPublishResult(null);
    // The response carries ids for new actions; later saves must send them.
    if (dirty) replaceForm(await save.mutateAsync({ id: scenario.id, body: form }));
    if (!thenPublish) return;
    try {
      await action.mutateAsync({ id: scenario.id, action: "publish" });
      setPublishResult({ published: true });
    } catch (err) {
      const errors = (err as { errors?: string[] }).errors;
      setPublishResult({ errors: errors?.length ? errors : [(err as Error).message] });
    }
  };

  const regen = (section: CaseSection) => (
    <Button type="button" className="px-2 py-1 text-xs" disabled={regenerating} onClick={() => setRegenTarget(section)} title={`Rewrite the ${SECTION_LABELS[section]} with the AI`}>
      Regenerate
    </Button>
  );

  const info = form.case_information;
  const refs = form.reference_sources;
  const p = form.patient_profile;
  const v = p.vital_signs;

  return (
    <div className="p-6 pb-28">
      <div className="mb-2 text-sm"><Link to={`/sim-patients/${scenario.id}`} className="text-blue-700 hover:underline">← {scenario.title}</Link></div>
      <PageHeader
        title="Review simulated patient case"
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            <StatusBadge status={scenario.status} label={STATUS_LABELS[scenario.status]} />
            <span>{scenario.course_name}{scenario.module_name ? ` · ${scenario.module_name}` : ""} · version {scenario.version}</span>
          </span>
        }
        actions={<Button disabled={regenerating} onClick={() => setRegenTarget("all")}>Regenerate whole case</Button>}
      />

      {job && !job.outcome?.ok && (
        <div className={`mb-4 rounded border px-4 py-3 ${job.outcome ? "border-red-200 bg-red-50" : "border-blue-200 bg-blue-50"}`}>
          <div className="mb-2 flex items-start justify-between gap-2 text-sm">
            <span className="font-medium">
              {job.section ? `Regenerating the ${SECTION_LABELS[job.section]}` : "Regenerating the whole case"}
            </span>
            {job.outcome && <button onClick={() => dismissSimJob(job.taskId)} className="text-gray-500 hover:text-gray-900" aria-label="Dismiss">✕</button>}
          </div>
          <SimJobProgress job={job} />
          {!job.outcome && <p className="mt-2 text-xs text-gray-600">Editing is paused until it finishes; the new content loads here automatically.</p>}
        </div>
      )}
      {justGenerated && !dirty && (
        <div className="mb-4 rounded border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
          The AI wrote this case from {form.source_documents.length} approved course documents. Check the clinical content against the MoH sources before publishing, and record the reviewers under Scenario metadata.
        </div>
      )}
      {scenario.attempt_count > 0 && (
        <div className="mb-4 rounded border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          {scenario.attempt_count} learners have already attempted this case. Saving creates version {scenario.version + 1}; past scores are kept, but decision-point reports will reflect the edited checklist.
        </div>
      )}
      {scenario.status === "published" && (
        <div className="mb-4 rounded border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">This case is live. Changes apply to new attempts as soon as you save.</div>
      )}

      <form
        key={formVersion}
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          doSave(false).catch(() => undefined);
        }}
      >
        <fieldset disabled={regenerating} className="space-y-4 disabled:opacity-60">
        <Card title="1. Case information" actions={regen("case_information")}>
          <div className="grid gap-4 md:grid-cols-6">
            <Field label="Case title" className="md:col-span-4"><Text value={info.case_title} onChange={(x) => edit((d) => { d.case_information.case_title = x; })} /></Field>
            <Field label="Difficulty" className="md:col-span-1">
              <select className={input} value={info.difficulty} onChange={(e) => edit((d) => { d.case_information.difficulty = e.target.value as Difficulty; })}>
                <option value="beginner">Beginner</option>
                <option value="intermediate">Intermediate</option>
                <option value="advanced">Advanced</option>
              </select>
            </Field>
            <Field label="Est. minutes" className="md:col-span-1"><NumberInput min={1} value={info.estimated_minutes} onChange={(x) => edit((d) => { d.case_information.estimated_minutes = x; })} /></Field>
            <Field label="CPD module" className="md:col-span-3"><Text value={info.cpd_module} onChange={(x) => edit((d) => { d.case_information.cpd_module = x; })} /></Field>
            <Field label="Clinical topic" className="md:col-span-3"><Text value={info.clinical_topic} onChange={(x) => edit((d) => { d.case_information.clinical_topic = x; })} /></Field>
            <Field label="Target learners" hint="Comma-separated" className="md:col-span-6"><CommaList value={info.target_learners} onChange={(x) => edit((d) => { d.case_information.target_learners = x; })} /></Field>
          </div>
          <div className="mt-4">
            <span className="text-xs font-medium text-gray-700">Learning objectives</span>
            <div className="mt-1"><StringList items={info.learning_objectives} addLabel="Add objective" onChange={(x) => edit((d) => { d.case_information.learning_objectives = x; })} /></div>
          </div>
        </Card>

        <Card title="2. Reference sources" actions={regen("reference_sources")}>
          <p className="mb-3 text-xs text-gray-500">All content must be based on approved MoH resources. Leave a field empty if the course doesn't cite one.</p>
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="MoH CPD module"><Text value={refs.cpd_module} onChange={(x) => edit((d) => { d.reference_sources.cpd_module = x; })} /></Field>
            <Field label="National guideline"><Text value={refs.national_guideline} onChange={(x) => edit((d) => { d.reference_sources.national_guideline = x; })} /></Field>
            <Field label="Job aid"><Text value={refs.job_aid} onChange={(x) => edit((d) => { d.reference_sources.job_aid = x; })} /></Field>
            <Field label="SOP / protocol"><Text value={refs.sop_protocol} onChange={(x) => edit((d) => { d.reference_sources.sop_protocol = x; })} /></Field>
            <Field label="Version / date"><Text value={refs.version_date} onChange={(x) => edit((d) => { d.reference_sources.version_date = x; })} /></Field>
          </div>
          {form.source_documents.length > 0 && (
            <details className="mt-4 text-sm">
              <summary className="cursor-pointer text-gray-600">Generated from {form.source_documents.length} approved course documents</summary>
              <ul className="mt-2 list-disc space-y-0.5 pl-5 text-gray-600">
                {form.source_documents.map((doc) => (
                  <li key={doc.document_id}>{[doc.section_name, doc.module_name, doc.title].filter(Boolean).join(" › ")}</li>
                ))}
              </ul>
            </details>
          )}
        </Card>

        <Card title="3. Patient profile" actions={regen("patient_profile")}>
          <div className="grid gap-4 md:grid-cols-6">
            <Field label="Name (fictional)" className="md:col-span-2"><Text value={p.name} onChange={(x) => edit((d) => { d.patient_profile.name = x; })} /></Field>
            <Field label="Age" className="md:col-span-1"><NumberInput min={0} max={120} value={p.age} onChange={(x) => edit((d) => { d.patient_profile.age = x; })} /></Field>
            <Field label="Sex" className="md:col-span-1"><Text value={p.sex} onChange={(x) => edit((d) => { d.patient_profile.sex = x; })} /></Field>
            <Field label="Occupation" className="md:col-span-2"><Text value={p.occupation} onChange={(x) => edit((d) => { d.patient_profile.occupation = x; })} /></Field>
            <Field label="Residence" className="md:col-span-2"><Text value={p.residence} onChange={(x) => edit((d) => { d.patient_profile.residence = x; })} /></Field>
            <Field label="Demeanor" className="md:col-span-2"><Text value={p.demeanor} onChange={(x) => edit((d) => { d.patient_profile.demeanor = x; })} /></Field>
            <Field label="Speaking style" className="md:col-span-2"><Text value={p.speaking_style} onChange={(x) => edit((d) => { d.patient_profile.speaking_style = x; })} /></Field>
            <Field label="Chief complaint" className="md:col-span-6"><Text value={p.chief_complaint} onChange={(x) => edit((d) => { d.patient_profile.chief_complaint = x; })} /></Field>
            <Field label="Presenting symptoms" hint="Comma-separated" className="md:col-span-6"><CommaList value={p.presenting_symptoms} onChange={(x) => edit((d) => { d.patient_profile.presenting_symptoms = x; })} /></Field>
            <Field label="History of present illness" className="md:col-span-6"><Text rows={3} value={p.history_of_present_illness} onChange={(x) => edit((d) => { d.patient_profile.history_of_present_illness = x; })} /></Field>
            <Field label="Past medical history" className="md:col-span-3"><Text rows={2} value={p.past_medical_history} onChange={(x) => edit((d) => { d.patient_profile.past_medical_history = x; })} /></Field>
            <Field label="Medication history" className="md:col-span-3"><Text rows={2} value={p.medication_history} onChange={(x) => edit((d) => { d.patient_profile.medication_history = x; })} /></Field>
            <Field label="Allergies" className="md:col-span-2"><Text value={p.allergies} onChange={(x) => edit((d) => { d.patient_profile.allergies = x; })} /></Field>
            <Field label="Obstetric history (if applicable)" className="md:col-span-4"><Text value={p.obstetric_history} onChange={(x) => edit((d) => { d.patient_profile.obstetric_history = x; })} /></Field>
            <Field label="Family / social history" className="md:col-span-6"><Text rows={2} value={p.family_social_history} onChange={(x) => edit((d) => { d.patient_profile.family_social_history = x; })} /></Field>
          </div>
          <h3 className="mb-2 mt-5 text-xs font-semibold uppercase tracking-wide text-gray-500">Vital signs</h3>
          <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-5">
            <Field label="Temperature"><Text value={v.temperature} onChange={(x) => edit((d) => { d.patient_profile.vital_signs.temperature = x; })} /></Field>
            <Field label="Blood pressure"><Text value={v.blood_pressure} onChange={(x) => edit((d) => { d.patient_profile.vital_signs.blood_pressure = x; })} /></Field>
            <Field label="Pulse"><Text value={v.pulse} onChange={(x) => edit((d) => { d.patient_profile.vital_signs.pulse = x; })} /></Field>
            <Field label="Respiratory rate"><Text value={v.respiratory_rate} onChange={(x) => edit((d) => { d.patient_profile.vital_signs.respiratory_rate = x; })} /></Field>
            <Field label="Oxygen saturation"><Text value={v.oxygen_saturation} onChange={(x) => edit((d) => { d.patient_profile.vital_signs.oxygen_saturation = x; })} /></Field>
          </div>
          <h3 className="mb-2 mt-5 text-xs font-semibold uppercase tracking-wide text-gray-500">Physical examination findings</h3>
          <StringList items={p.physical_examination} addLabel="Add finding" onChange={(x) => edit((d) => { d.patient_profile.physical_examination = x; })} />
        </Card>

        <Card title="4. Case introduction" actions={regen("case_introduction")}>
          <Field label="Shown to the learner before the encounter"><Text rows={3} value={form.case_introduction} onChange={(x) => edit((d) => { d.case_introduction = x; })} /></Field>
          <div className="mt-4 flex items-end gap-2">
            <Field label="Patient's opening statement" hint="The patient's first words when the learner starts." className="flex-1">
              <Text rows={2} value={form.opening_statement} onChange={(x) => edit((d) => { d.opening_statement = x; })} />
            </Field>
            <div className="pb-5">{regen("opening_statement")}</div>
          </div>
        </Card>

        <Card title={`5. Branching dialogue (${form.dialogue_steps.length} steps)`} actions={regen("dialogue_steps")}>
          <p className="mb-3 text-xs text-gray-500">
            Reference dialogue for reviewers: the patient's line at each step, the learner options, the patient's reply and the score. Learners chat freely; they're scored on the critical decision points below.
          </p>
          <ol className="space-y-4">
            {form.dialogue_steps.map((step, si) => (
              <li key={si} className="rounded border p-3">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-xs font-semibold text-gray-500">Step {si + 1}</span>
                  <RemoveButton label="Remove step" onClick={() => edit((d) => { d.dialogue_steps.splice(si, 1); })} />
                </div>
                <Field label="Patient statement"><Text value={step.patient_statement} onChange={(x) => edit((d) => { d.dialogue_steps[si].patient_statement = x; })} /></Field>
                <div className="mt-3 space-y-2">
                  {step.options.map((o, oi) => (
                    <div key={oi} className="grid gap-2 rounded bg-gray-50 p-2 md:grid-cols-[2rem_1fr_1fr_5rem_auto]">
                      <span className="pt-1.5 text-sm font-semibold">{String.fromCharCode(65 + oi)}.</span>
                      <Text value={o.text} placeholder="Learner option" onChange={(x) => edit((d) => { d.dialogue_steps[si].options[oi].text = x; })} />
                      <Text value={o.patient_response} placeholder="Patient response" onChange={(x) => edit((d) => { d.dialogue_steps[si].options[oi].patient_response = x; })} />
                      <select aria-label="Score" className={input} value={o.score} onChange={(e) => edit((d) => { d.dialogue_steps[si].options[oi].score = Number(e.target.value); })}>
                        {[2, 1, 0, -1, -2].map((n) => <option key={n} value={n}>{n > 0 ? `+${n}` : n}</option>)}
                      </select>
                      <RemoveButton label="Remove option" onClick={() => edit((d) => { d.dialogue_steps[si].options.splice(oi, 1); })} />
                      <div className="md:col-start-2 md:col-end-5">
                        <Text value={o.feedback} placeholder="Immediate feedback (why, and the MoH recommendation)" onChange={(x) => edit((d) => { d.dialogue_steps[si].options[oi].feedback = x; })} />
                      </div>
                    </div>
                  ))}
                  {step.options.length < 6 && (
                    <AddButton onClick={() => edit((d) => { d.dialogue_steps[si].options.push({ key: "", text: "", patient_response: "", score: 0, feedback: "" }); })}>Add option</AddButton>
                  )}
                </div>
              </li>
            ))}
          </ol>
          <div className="mt-3">
            <AddButton onClick={() => edit((d) => { d.dialogue_steps.push({ patient_statement: "", options: [] }); })}>Add step</AddButton>
          </div>
        </Card>

        <Card
          title={<span>6. Critical decision points <span className={`ml-2 rounded px-1.5 py-0.5 text-xs ${total === 100 ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}>{total}/100</span></span>}
          actions={regen("critical_decision_points")}
        >
          <p className="mb-3 text-xs text-gray-500">
            How learners are scored. An expected action is hit when the learner's message matches its keywords or means the same thing; the patient then reveals its text. Action points must add up to the domain score.
          </p>
          <div className="space-y-4">
            {domains.map((dom, di) => {
              const sum = dom.actions.reduce((acc, a) => acc + (Number(a.points) || 0), 0);
              return (
                <section key={dom.domain} className="rounded border">
                  <header className="flex flex-wrap items-center gap-3 border-b bg-gray-50 px-3 py-2">
                    <h3 className="text-sm font-semibold">{DOMAIN_LABELS[dom.domain]}</h3>
                    <label className="flex items-center gap-1 text-xs text-gray-600">
                      Score
                      <input type="number" min={0} max={100} className="w-16 rounded border px-1.5 py-0.5 text-sm" value={dom.score} onChange={(e) => edit((d) => { d.critical_decision_points[di].score = Number(e.target.value); })} />
                    </label>
                    <span className={`text-xs ${sum === dom.score ? "text-gray-500" : "font-medium text-red-600"}`}>actions total {sum}</span>
                    <div className="min-w-[12rem] flex-1">
                      <Text value={dom.expected_action} placeholder="Expected action (summary)" onChange={(x) => edit((d) => { d.critical_decision_points[di].expected_action = x; })} />
                    </div>
                  </header>
                  <ol className="divide-y">
                    {dom.actions.map((a, ai) => (
                      <ActionRow
                        key={a.id ?? `new-${ai}`}
                        action={a}
                        onChange={(patch) => edit((d) => { Object.assign(d.critical_decision_points[di].actions[ai], patch); })}
                        onRemove={() => edit((d) => { d.critical_decision_points[di].actions.splice(ai, 1); })}
                      />
                    ))}
                  </ol>
                  <div className="px-3 py-2">
                    <AddButton onClick={() => edit((d) => { d.critical_decision_points[di].actions.push({ description: "", trigger_keywords: [], reveals: "", points: 0, is_critical: false, feedback_if_missed: "" }); })}>
                      Add action
                    </AddButton>
                  </div>
                </section>
              );
            })}
          </div>
        </Card>

        <Card title="7. Branching logic" actions={regen("performance_bands")}>
          <div className="space-y-2">
            {form.performance_bands.map((b, bi) => (
              <div key={bi} className="grid items-center gap-2 md:grid-cols-[5rem_1rem_5rem_1fr_1fr_auto]">
                <NumberInput min={0} max={100} value={b.min_score} onChange={(x) => edit((d) => { d.performance_bands[bi].min_score = x; })} />
                <span className="text-center text-gray-400">–</span>
                <NumberInput min={0} max={100} value={b.max_score} onChange={(x) => edit((d) => { d.performance_bands[bi].max_score = x; })} />
                <Text value={b.level} placeholder="Performance level" onChange={(x) => edit((d) => { d.performance_bands[bi].level = x; })} />
                <Text value={b.pathway} placeholder="Pathway" onChange={(x) => edit((d) => { d.performance_bands[bi].pathway = x; })} />
                <RemoveButton label="Remove band" onClick={() => edit((d) => { d.performance_bands.splice(bi, 1); })} />
              </div>
            ))}
            <AddButton onClick={() => edit((d) => { d.performance_bands.push({ min_score: 0, max_score: 0, level: "", pathway: "" }); })}>Add band</AddButton>
          </div>
        </Card>

        <div className="grid gap-4 lg:grid-cols-2">
          <Card title="8. AI feedback rules" actions={regen("feedback_rules")}>
            <StringList items={form.feedback_rules} addLabel="Add rule" onChange={(x) => edit((d) => { d.feedback_rules = x; })} />
          </Card>
          <Card title="9. Key learning points" actions={regen("key_learning_points")}>
            <StringList items={form.key_learning_points} addLabel="Add point" rows={2} onChange={(x) => edit((d) => { d.key_learning_points = x; })} />
          </Card>
          <Card title="10. Learner reflection" actions={regen("reflection_prompts")}>
            <StringList items={form.reflection_prompts} addLabel="Add prompt" onChange={(x) => edit((d) => { d.reflection_prompts = x; })} />
          </Card>
          <Card title="11. Scenario metadata">
            <dl className="mb-4 grid grid-cols-2 gap-2 text-sm">
              <div><dt className="text-xs text-gray-500">Scenario ID</dt><dd>SP-{String(scenario.id).padStart(4, "0")}</dd></div>
              <div><dt className="text-xs text-gray-500">Version</dt><dd>{scenario.version}</dd></div>
              <div><dt className="text-xs text-gray-500">Author</dt><dd className="truncate">{scenario.authored_by_email ?? "—"}</dd></div>
              <div><dt className="text-xs text-gray-500">Status</dt><dd>{STATUS_LABELS[scenario.status]}</dd></div>
            </dl>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Clinical reviewer (MoH)"><Text value={form.metadata.clinical_reviewer} onChange={(x) => edit((d) => { d.metadata.clinical_reviewer = x; })} /></Field>
              <Field label="Technical reviewer (JSI/EAII)"><Text value={form.metadata.technical_reviewer} onChange={(x) => edit((d) => { d.metadata.technical_reviewer = x; })} /></Field>
              <Field label="Approval date"><input type="date" className={input} value={form.metadata.approval_date} onChange={(e) => edit((d) => { d.metadata.approval_date = e.target.value; })} /></Field>
              <Field label="Next review date"><input type="date" className={input} value={form.metadata.next_review_date} onChange={(e) => edit((d) => { d.metadata.next_review_date = e.target.value; })} /></Field>
            </div>
          </Card>
        </div>

        </fieldset>
        <div className="fixed inset-x-0 bottom-0 z-10 border-t bg-white/95 px-6 py-3 backdrop-blur md:left-56">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="text-sm text-gray-600">
              Total score <span className={`font-semibold tabular-nums ${total === 100 ? "" : "text-red-600"}`}>{total}/100</span>
              {dirty && <span className="ml-3 text-amber-700">Unsaved changes</span>}
            </span>
            <div className="flex gap-2">
              <Link to={`/sim-patients/${scenario.id}`} className="inline-flex items-center rounded px-3 py-1.5 text-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">Close</Link>
              <Button type="submit" loading={save.isPending && !action.isPending} disabled={!dirty || regenerating}>Save</Button>
              {scenario.status !== "published" && (
                <Button type="button" variant="primary" loading={action.isPending} disabled={save.isPending || regenerating} onClick={() => doSave(true).catch(() => undefined)}>
                  {dirty ? "Save and publish" : "Publish"}
                </Button>
              )}
            </div>
          </div>
          <ErrorBanner error={save.error} />
          {publishResult?.errors && <ActionErrors error={{ errors: publishResult.errors }} />}
          {publishResult?.published && <p className="mt-2 text-sm text-green-700">Published. Learners can start this case now.</p>}
        </div>
      </form>

      {regenTarget && (
        <RegenerateDialog
          target={regenTarget}
          dirty={dirty}
          pending={regenerate.isPending}
          error={regenerate.error}
          onClose={() => {
            regenerate.reset();
            setRegenTarget(null);
          }}
          onRun={(instructions) =>
            regenerate.mutate(
              { id: scenario.id, section: regenTarget === "all" ? undefined : regenTarget, instructions },
              {
                onSuccess: (res) => {
                  if (res.mode === "inline") {
                    replaceForm(res.scenario);
                    setPublishResult(null);
                  } else {
                    addSimJob({
                      taskId: res.task_id,
                      kind: "regenerate",
                      label: scenario.title,
                      scenarioId: scenario.id,
                      section: regenTarget === "all" ? null : regenTarget,
                    });
                  }
                  setRegenTarget(null);
                },
              }
            )
          }
        />
      )}
    </div>
  );
}

function ActionRow({ action: a, onChange, onRemove }: { action: ExpectedAction; onChange: (patch: Partial<ExpectedAction>) => void; onRemove: () => void }) {
  return (
    <li className={`grid gap-2 px-3 py-3 md:grid-cols-6 ${a.is_critical ? "bg-red-50/40" : ""}`}>
      <Field label="Expected action" className="md:col-span-4"><Text value={a.description} onChange={(x) => onChange({ description: x })} /></Field>
      <Field label="Points" className="md:col-span-1"><NumberInput min={0} value={a.points} onChange={(x) => onChange({ points: x })} /></Field>
      <div className="flex items-end justify-between gap-2 pb-2 md:col-span-1">
        <label className="flex items-center gap-1.5 text-sm">
          <input type="checkbox" checked={a.is_critical} onChange={(e) => onChange({ is_critical: e.target.checked })} />
          Critical
        </label>
        <RemoveButton label="Remove action" onClick={onRemove} />
      </div>
      <Field label="Trigger keywords" hint="Comma-separated phrases a learner might type" className="md:col-span-6">
        <CommaList value={a.trigger_keywords} onChange={(x) => onChange({ trigger_keywords: x })} />
      </Field>
      <Field label="Patient reveals / finding shown" className="md:col-span-3"><Text rows={2} value={a.reveals} onChange={(x) => onChange({ reveals: x })} /></Field>
      <Field label="Feedback if missed" className="md:col-span-3"><Text rows={2} value={a.feedback_if_missed} onChange={(x) => onChange({ feedback_if_missed: x })} /></Field>
    </li>
  );
}

function RegenerateDialog({ target, dirty, pending, error, onClose, onRun }: {
  target: CaseSection | "all";
  dirty: boolean;
  pending: boolean;
  error: unknown;
  onClose: () => void;
  onRun: (instructions: string) => void;
}) {
  const [instructions, setInstructions] = useState("");
  const label = target === "all" ? "the whole case" : `the ${SECTION_LABELS[target]}`;

  return (
    <Modal
      title={`Regenerate ${label}`}
      onClose={pending ? () => undefined : onClose}
      footer={
        <>
          <Button onClick={onClose} disabled={pending}>Cancel</Button>
          <Button
            variant="primary"
            loading={pending}
            onClick={() => onRun(instructions.trim())}
          >
            Regenerate
          </Button>
        </>
      }
    >
      <p className="text-sm text-gray-700">
        The AI rewrites {label} from the course's approved content{target === "all" ? "" : ", keeping the rest of the case"}.
        {target === "all" && " Reviewer names and dates are kept."}
      </p>
      {dirty && <p className="mt-2 text-sm text-amber-700">You have unsaved changes. They'll be replaced by the regenerated content.</p>}
      <label className="mt-4 block">
        <span className="text-xs font-medium text-gray-700">{target === "all" ? "Clinical focus or instructions (optional)" : "Instructions (optional)"}</span>
        <textarea
          rows={3}
          className={`${input} mt-1`}
          value={instructions}
          maxLength={2000}
          onChange={(e) => setInstructions(e.target.value)}
          placeholder={target === "all" ? "e.g. focus on postpartum haemorrhage" : "e.g. make the options harder to tell apart"}
        />
      </label>
      <p className="mt-3 text-xs text-gray-500">It runs on the worker and usually takes 1–3 minutes; you'll see its progress on this page.</p>
      {error != null && <ActionErrors error={error} />}
    </Modal>
  );
}
